import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImageOff } from "lucide-react";
import { useState } from "react";
import type {
  CollectionRef,
  FieldDefinition,
  Id,
  Metafield,
  ProductImage,
  ProductWithVariants,
  Variant,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CATALOG_FIELDS } from "@/modules/catalog/fields";
import { metafieldLabel, metafieldText } from "@/modules/catalog/metafields";
import { HsnEditor } from "@/modules/catalog/screens/HsnEditor";
import { ImageLightbox } from "@/modules/catalog/screens/ImageLightbox";
import { ProductEditForm } from "@/modules/catalog/screens/ProductEditForm";
import {
  PriceSummary,
  ProductTags,
  RatingStars,
} from "@/modules/catalog/screens/ProductSummary";

/** Reused so a variant's price and compare-at price format the same way the
 * list's price range does — see `PRICE_FIELD` in `ProductList.tsx`. */
const PRICE_FIELD: FieldDefinition = {
  name: "price",
  label: "Price",
  type: "money",
};

/** `CATALOG_FIELDS` already owns the status labels; reused here so a status
 * reads the same word on the list and on the product it belongs to. */
const STATUS_FIELD = CATALOG_FIELDS.find((field) => field.name === "status");

function Gallery({ images, title }: { images: ProductImage[]; title: string }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sorted = images.slice().sort((a, b) => a.position - b.position);

  if (sorted.length === 0) {
    return (
      <div
        className="bg-muted text-muted-foreground flex h-40 w-40 items-center justify-center rounded-md"
        aria-hidden
      >
        <ImageOff className="size-6" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* Straight from the channel's CDN, same as the list thumbnail
            (FR-CAT-13) — nothing here is fetched, resized or re-hosted. */}
        {sorted.map((image, position) => (
          <button
            key={image.id}
            type="button"
            aria-label={`Open image ${String(position + 1)} of ${String(sorted.length)}`}
            onClick={() => {
              setOpenIndex(position);
            }}
            className="focus-visible:ring-ring overflow-hidden rounded-md border transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:outline-none"
          >
            <img
              src={image.url}
              alt={image.altText ?? title}
              loading="lazy"
              className="size-40 object-cover"
            />
          </button>
        ))}
      </div>
      <ImageLightbox
        images={sorted}
        title={title}
        index={openIndex}
        onIndexChange={setOpenIndex}
      />
    </>
  );
}

function CollectionBadges({ collections }: { collections: CollectionRef[] }) {
  if (collections.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">
        Not in a collection.
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {collections.map((collection) => (
        <Badge key={collection.id} variant="outline" className="font-normal">
          {collection.title}
        </Badge>
      ))}
    </div>
  );
}

/**
 * The metafields the API mirrors (#308, FR-CAT-5) — Shopify's category
 * attributes, the review figures, and the merchant's own `custom` ones.
 *
 * Shown by their display value where there is one. A reference the channel
 * would not resolve has only its stored gids to show; those are kept, muted
 * and in monospace, with a line saying what they are, so a list of ids reads
 * as "the names are not available" rather than as corrupted data.
 */
function MetafieldList({ metafields }: { metafields: Metafield[] }) {
  if (metafields.length === 0) {
    return (
      <span className="text-muted-foreground text-sm">No metafields.</span>
    );
  }

  const anyUnreadable = metafields.some(
    (field) => !metafieldText(field).readable,
  );

  return (
    <div className="space-y-2">
      <dl className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        {metafields.map((field) => {
          const shown = metafieldText(field);
          return (
            <div
              key={`${field.namespace}.${field.key}`}
              className="col-span-2 grid grid-cols-subgrid"
            >
              <dt>
                <span className="font-medium">{metafieldLabel(field)}</span>
                <span className="text-muted-foreground block font-mono text-xs break-all">
                  {field.namespace}.{field.key}
                </span>
              </dt>
              <dd
                className={
                  shown.readable
                    ? "break-words"
                    : "text-muted-foreground font-mono text-xs break-all"
                }
              >
                {shown.text}
              </dd>
            </div>
          );
        })}
      </dl>
      {anyUnreadable ? (
        <p className="text-muted-foreground text-xs">
          Values shown as ids are references Shopify did not return a name for.
        </p>
      ) : null}
    </div>
  );
}

/** Grams, as the channel gives them — see `weightGrams` on `Variant`. */
function formatWeight(grams: number | null): string {
  return grams === null ? "—" : `${String(grams)} g`;
}

function VariantRow({
  variant,
  fallbackImageUrl,
}: {
  variant: Variant;
  /**
   * The product's featured image. `Variant.imageUrl` is null unless the
   * merchant set a variant-specific image, and the domain documents the
   * product's as the fallback — so a single-variant product shows its own
   * picture rather than a placeholder that reads as missing data.
   */
  fallbackImageUrl: string | null;
}) {
  const imageUrl = variant.imageUrl ?? fallbackImageUrl;

  return (
    <tr className="border-b align-top last:border-b-0">
      <td className="px-3 py-2">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={variant.title}
            loading="lazy"
            className="size-10 rounded object-cover"
          />
        ) : (
          <div
            className="bg-muted text-muted-foreground flex size-10 items-center justify-center rounded"
            aria-hidden
          >
            <ImageOff className="size-4" />
          </div>
        )}
      </td>
      {/* "Default Title" is the real name of the only variant on almost
          every product here — rendered plainly rather than hidden, so a
          single-variant product's table does not look like a broken
          multi-variant one with the variant column empty. */}
      <td className="px-3 py-2">{variant.title}</td>
      <td className="px-3 py-2 tabular-nums">{variant.sku ?? "—"}</td>
      <td className="px-3 py-2 tabular-nums">{variant.barcode ?? "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {renderCell(PRICE_FIELD, variant.price)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {variant.compareAtPrice
          ? renderCell(PRICE_FIELD, variant.compareAtPrice)
          : "—"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {formatWeight(variant.weightGrams)}
      </td>
      <td className="px-3 py-2">
        <HsnEditor variant={variant} />
      </td>
    </tr>
  );
}

function VariantTable({
  variants,
  fallbackImageUrl,
}: {
  variants: Variant[];
  fallbackImageUrl: string | null;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max border-collapse text-sm">
        <thead>
          <tr className="border-b">
            <th className="px-3 py-2 text-left font-medium">Image</th>
            <th className="px-3 py-2 text-left font-medium">Variant</th>
            <th className="px-3 py-2 text-left font-medium">SKU</th>
            <th className="px-3 py-2 text-left font-medium">Barcode</th>
            <th className="px-3 py-2 text-right font-medium">Price</th>
            <th className="px-3 py-2 text-right font-medium">Compare-at</th>
            <th className="px-3 py-2 text-right font-medium">Weight</th>
            <th className="px-3 py-2 text-left font-medium">HSN code</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((variant) => (
            <VariantRow
              key={variant.id}
              variant={variant}
              fallbackImageUrl={fallbackImageUrl}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductDetailBody({ product }: { product: ProductWithVariants }) {
  return (
    <div className="space-y-4">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{product.title}</h2>
          {STATUS_FIELD ? renderCell(STATUS_FIELD, product.status) : null}
        </div>
        <p className="text-muted-foreground text-sm">
          {[product.vendor, product.productType].filter(Boolean).join(" · ") ||
            "No vendor or product type on file."}
        </p>
        <ProductTags tags={product.tags} />
        <RatingStars metafields={product.metafields} />
        <PriceSummary variants={product.variants} />
        <ProductEditForm product={product} />
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Images</CardTitle>
        </CardHeader>
        <CardContent>
          <Gallery images={product.images} title={product.title} />
        </CardContent>
      </Card>

      {product.descriptionHtml ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Description</CardTitle>
          </CardHeader>
          <CardContent>
            {/* `descriptionHtml` is Shopify's own markup, not user input —
                rendered as HTML because that is what FR-CAT-4 asks for and
                what the channel actually stores; escaping it to plain text
                would show a customer-facing product page's raw tags. */}
            <div
              className="max-w-none text-sm [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Collections</CardTitle>
        </CardHeader>
        <CardContent>
          <CollectionBadges collections={product.collections} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Metafields</CardTitle>
        </CardHeader>
        <CardContent>
          <MetafieldList metafields={product.metafields} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Variants ({product.variants.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <VariantTable
            variants={product.variants}
            fallbackImageUrl={product.featuredImageUrl}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One product, with its variants, images, reviews, collections and mirrored
 * metafields (M6-2, M6-3, #308). There is no `/catalog/:id` route — `config/modules.ts` does
 * not declare one and this directory cannot add one — so `ProductList`
 * renders this in place of the table, the same way `ContactList` renders
 * `ContactDetail`, and `onBack` hands control back to it.
 */
export function ProductDetail({
  productId,
  onBack,
}: {
  productId: Id;
  onBack?: () => void;
}) {
  const data = useDataSource();

  const query = useQuery({
    queryKey: ["catalog", "products", "detail", productId],
    queryFn: () => data.catalog.getProduct(productId),
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Optional: the drawer this renders inside carries its own close, and two
        ways out of one panel side by side each look like they do something
        different.
      */}
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to list
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {query.isPending ? (
          <TableSkeleton rows={4} />
        ) : query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : query.data === null ? (
          <EmptyState
            title="Product not found"
            description="It may have been deleted since this list was loaded."
          />
        ) : (
          <ProductDetailBody product={query.data} />
        )}
      </div>
    </div>
  );
}
